import { Link } from "react-router-dom";
import { useState } from "react";
import { 
  Box, 
  Avatar, 
  LinearProgress, 
  Typography, 
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import useFetchDataWithPagination from "../../../utility/useFetchDataWithPagination";
import usePostData from "../../../utility/usePostData";

const ArticleList = () => {
    const [searchTerm, setSearchTerm] = useState("");
    const [previewDialog, setPreviewDialog] = useState(false);
    const [selectedArticle, setSelectedArticle] = useState(null);
    
    const { data: articles, isLoading, error, refreshSearch, paginate } = useFetchDataWithPagination("articles");
    const articlesList = articles?.data?.data;
    const { submitData } = usePostData();

    const deleteArticle = (id) => {
        if (window.confirm("Êtes-vous sûr de vouloir supprimer cet article ?")) {
            submitData("articles/" + id, null, 'DELETE');
            refreshSearch();
        }
    };

    const handlePreview = (article) => {
        setSelectedArticle(article);
        setPreviewDialog(true);
    };

    const filteredArticles = articlesList?.filter(article => 
        article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.body.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const stripHtml = (html) => {
        const tmp = document.createElement("DIV");
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || "";
    };

    return ( 
        <Box sx={{ width: "95%", margin: "auto", mt: 3, mb: 3 }}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                {isLoading && (
                    <LinearProgress
                        sx={{
                            backgroundColor: "#e0e0e0",
                            "& .MuiLinearProgress-bar": {
                                backgroundColor: "#00a652"
                            }
                        }}
                    />
                )}

                <Stack 
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between" 
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    spacing={2}
                    sx={{ mb: 3 }}
                >
                    <Typography 
                        variant="h5" 
                        sx={{ 
                            color: '#00a652',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}
                    >
                        📝 Liste des Articles
                        <Chip 
                            label={filteredArticles?.length || 0} 
                            size="small" 
                            color="success"
                        />
                    </Typography>

                    <Stack direction="row" spacing={2}>
                        <TextField
                            size="small"
                            placeholder="Rechercher..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                )
                            }}
                            sx={{ minWidth: 200 }}
                        />

                        <Button
                            component={Link}
                            to="/admin-article-add"
                            variant="contained"
                            startIcon={<AddIcon />}
                            sx={{
                                backgroundColor: '#00a652',
                                '&:hover': { backgroundColor: '#008c46' }
                            }}
                        >
                            Nouvel Article
                        </Button>
                    </Stack>
                </Stack>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#00a652' }}>
                                <TableCell sx={{ color: 'white', fontWeight: 600 }}>#</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Image</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Titre</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Extrait</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Langues</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Date</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 600 }} align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredArticles && filteredArticles.length > 0 ? (
                                filteredArticles.map((article, index) => (
                                    <TableRow 
                                        key={article.id}
                                        hover
                                        sx={{
                                            '&:hover': {
                                                backgroundColor: 'rgba(0,166,82,0.04) !important'
                                            }
                                        }}
                                    >
                                        <TableCell sx={{ fontWeight: 500 }}>
                                            {index + 1}
                                        </TableCell>
                                        <TableCell>
                                            <Avatar
                                                alt={article.image_alt || article.title}
                                                src={article.image_source_url}
                                                variant="rounded"
                                                sx={{ 
                                                    width: 80, 
                                                    height: 60,
                                                    border: '2px solid #e0e0e0',
                                                    objectFit: 'cover'
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 250 }}>
                                            <Typography variant="body2" fontWeight={600}>
                                                {article.title}
                                            </Typography>
                                            {article.slug && (
                                                <Typography variant="caption" color="text.secondary">
                                                    /{article.slug}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 300 }}>
                                            <Typography 
                                                variant="body2" 
                                                color="text.secondary"
                                                sx={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                }}
                                            >
                                                {stripHtml(article.body).substring(0, 100)}...
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.5}>
                                                <Chip 
                                                    label="FR" 
                                                    size="small" 
                                                    color="primary" 
                                                    variant="outlined"
                                                />
                                                {article.title_en && (
                                                    <Chip 
                                                        label="EN" 
                                                        size="small" 
                                                        color="secondary" 
                                                        variant="outlined"
                                                    />
                                                )}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {new Date(article.created_at).toLocaleDateString('fr-FR', {
                                                    day: '2-digit',
                                                    month: 'short',
                                                    year: 'numeric'
                                                })}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Modifié: {new Date(article.updated_at).toLocaleDateString('fr-FR')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Stack direction="row" spacing={0.5} justifyContent="center">
                                                <Tooltip title="Aperçu">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handlePreview(article)}
                                                        sx={{ 
                                                            color: '#1976d2',
                                                            '&:hover': { backgroundColor: 'rgba(25,118,210,0.04)' }
                                                        }}
                                                    >
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                
                                                <Tooltip title="Modifier">
                                                    <IconButton
                                                        component={Link}
                                                        to={`/admin-article-edit/${article.id}`}
                                                        size="small"
                                                        sx={{ 
                                                            color: '#00a652',
                                                            '&:hover': { backgroundColor: 'rgba(0,166,82,0.04)' }
                                                        }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>

                                                <Tooltip title="Supprimer">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => deleteArticle(article.id)}
                                                        sx={{ 
                                                            color: '#ec1a20',
                                                            '&:hover': { backgroundColor: 'rgba(236,26,32,0.04)' }
                                                        }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                        <Typography variant="h6" color="text.secondary">
                                            {searchTerm ? "Aucun article trouvé" : "Aucun article disponible"}
                                        </Typography>
                                        {!searchTerm && (
                                            <Button
                                                component={Link}
                                                to="/admin-article-add"
                                                variant="contained"
                                                startIcon={<AddIcon />}
                                                sx={{ 
                                                    mt: 2,
                                                    backgroundColor: '#00a652',
                                                    '&:hover': { backgroundColor: '#008c46' }
                                                }}
                                            >
                                                Créer votre premier article
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    {paginate && paginate()}
                </Box>
            </Paper>

            {/* Dialog d'aperçu */}
            <Dialog 
                open={previewDialog} 
                onClose={() => setPreviewDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ bgcolor: '#00a652', color: 'white' }}>
                    Aperçu de l'article
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    {selectedArticle && (
                        <Box>
                            {selectedArticle.image && (
                                <Box
                                    component="img"
                                    src={selectedArticle.image_source_url}
                                    alt={selectedArticle.image_alt || selectedArticle.title}
                                    sx={{
                                        width: '100%',
                                        maxHeight: 300,
                                        objectFit: 'cover',
                                        borderRadius: 1,
                                        mb: 2
                                    }}
                                />
                            )}
                            <Typography variant="h5" gutterBottom fontWeight={600}>
                                {selectedArticle.title}
                            </Typography>
                            {selectedArticle.title_en && (
                                <Typography variant="h6" color="text.secondary" gutterBottom>
                                    {selectedArticle.title_en}
                                </Typography>
                            )}
                            <Typography 
                                variant="body1" 
                                sx={{ mt: 2 }}
                                dangerouslySetInnerHTML={{ __html: selectedArticle.body }}
                            />
                            {selectedArticle.image_caption && (
                                <Typography variant="caption" display="block" sx={{ mt: 2, fontStyle: 'italic' }}>
                                    {selectedArticle.image_caption}
                                </Typography>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviewDialog(false)}>
                        Fermer
                    </Button>
                    {selectedArticle && (
                        <Button
                            component={Link}
                            to={`/admin-article-edit/${selectedArticle.id}`}
                            variant="contained"
                            sx={{ bgcolor: '#00a652', '&:hover': { bgcolor: '#008c46' } }}
                        >
                            Modifier
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
};
 
export default ArticleList;